import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Box, TextField, Button, Typography, Alert, Fade } from '@mui/material';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { login, user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  // Redirect if already logged in, but only after loading is complete
  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(() => {
        source.cancel('Request timed out');
      }, 10000); // 10-second timeout

      const res = await axios.post(
        'https://apibackoffice.lalivraison.ma/api/auth/login',
        { email, password },
        { cancelToken: source.token }
      );

      clearTimeout(timeout);
      await login(res.data.token);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (axios.isCancel(err)) {
        setError('Request timed out. Please try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to log in. Please try again.');
      }
      console.error('Login error:', err);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(145deg, #21CCBB 30%, #E0F7FA 100%)', // Updated gradient with lalivraison color
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle animated background particles effect */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
          animation: 'pulse 10s infinite ease-in-out',
          '@keyframes pulse': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '50%': { transform: 'scale(1.2)', opacity: 0.4 },
            '100%': { transform: 'scale(1)', opacity: 0.8 },
          },
        }}
      />

      <Fade in timeout={1200}>
        <Box
          sx={{
            width: { xs: '90%', sm: 450 }, // Responsive width
            p: { xs: 3, sm: 4 },
            bgcolor: 'rgba(255, 255, 255, 0.95)', // Glassmorphism effect
            backdropFilter: 'blur(12px)',
            borderRadius: 4,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            transition: 'transform 0.4s ease-in-out, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)', // Lift effect on hover
              boxShadow: '0 15px 50px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          {/* Logo and Welcome Message */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img
              src="https://dispatchv2.lalivraison.ma/logolaliv.png"
              alt="lalivraison.ma logo"
              style={{ width: '150px', marginBottom: '16px' }}
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{
                mb: 2,
                bgcolor: 'rgba(245, 247, 250, 0.9)',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#21CCBB', // Updated to lalivraison color
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#21CCBB',
                    boxShadow: '0 0 8px rgba(33, 204, 187, 0.3)', // Updated shadow color
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': {
                    color: '#21CCBB', // Updated to lalivraison color
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{
                mb: 3,
                bgcolor: 'rgba(245, 247, 250, 0.9)',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  },
                  '&:hover fieldset': {
                    borderColor: '#21CCBB', // Updated to lalivraison color
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#21CCBB',
                    boxShadow: '0 0 8px rgba(33, 204, 187, 0.3)', // Updated shadow color
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#666',
                  '&.Mui-focused': {
                    color: '#21CCBB', // Updated to lalivraison color
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                bgcolor: '#21CCBB', // Updated to lalivraison color
                color: 'white',
                fontWeight: 'bold',
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#1EBBAA', // Slightly darker shade for hover
                  transform: 'scale(1.02)',
                  boxShadow: '0 8px 20px rgba(33, 204, 187, 0.3)', // Updated shadow color
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              Se connecter
            </Button>
          </form>

          {/* Footer Note */}
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', mt: 2, color: '#888' }}
          >
            © {new Date().getFullYear()} lalivraison.ma - All Rights Reserved
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default Login;